import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BarChart } from './_finHelpers';

// Annuity Calculator (accumulation phase) — mirrors image
export default function AnnuityCalc() {
  const [start, setStart] = useState(20000);
  const [annualAdd, setAnnualAdd] = useState(10000);
  const [monthlyAdd, setMonthlyAdd] = useState(0);
  const [when, setWhen] = useState('beginning');
  const [rate, setRate] = useState(6);
  const [years, setYears] = useState(10);
  const [view, setView] = useState('annual');

  const c = useMemo(() => {
    const rMon = Math.pow(1 + rate/100, 1/12) - 1;
    let bal = start;
    const monthly = []; const annual = [];
    let yAdd = 0, yInt = 0;
    for (let y = 1; y <= years; y++) {
      // Annual addition at beginning of year
      if (when === 'beginning') { bal += annualAdd; yAdd += annualAdd; }
      for (let m = 0; m < 12; m++) {
        if (when === 'beginning') { bal += monthlyAdd; yAdd += monthlyAdd; }
        const int = bal * rMon;
        bal += int; yInt += int;
        if (when === 'end') { bal += monthlyAdd; yAdd += monthlyAdd; }
        monthly.push({ month: `Y${y}M${m+1}`, deposit: monthlyAdd + (m===0 && when==='beginning' ? annualAdd : 0) + (m===11 && when==='end' ? annualAdd : 0), interest: int, balance: bal });
      }
      if (when === 'end') { bal += annualAdd; yAdd += annualAdd; }
      annual.push({ year: y, deposit: yAdd, interest: yInt, balance: bal });
      yAdd = 0; yInt = 0;
    }
    const totalAdd = (annualAdd + monthlyAdd * 12) * years;
    const totalReturn = bal - start - totalAdd;
    return { end: bal, totalAdd, totalReturn, monthly, annual };
  }, [start, annualAdd, monthlyAdd, when, rate, years]);

  return (
    <CalcShell look="cnet" title="Annuity Calculator" subtitle="Accumulation phase — project the future balance of an annuity with periodic additions."
      about={<><p>An <b>annuity</b> is a series of equal payments made at regular intervals. This calculator models the accumulation phase, showing how the balance grows over time with periodic additions and compound returns.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Starting principal</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={start} onChange={e=>setStart(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Annual addition</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={annualAdd} onChange={e=>setAnnualAdd(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Monthly addition</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthlyAdd} onChange={e=>setMonthlyAdd(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Add at each period's</label>
            <div className="flex flex-col gap-1 mt-1 text-sm">
              <label className="flex items-center gap-2"><input type="radio" checked={when==='beginning'} onChange={()=>setWhen('beginning')}/> beginning (annuity due)</label>
              <label className="flex items-center gap-2"><input type="radio" checked={when==='end'} onChange={()=>setWhen('end')}/> end (ordinary/immediate annuity)</label>
            </div>
          </div>
          <div className="field"><label>Annual growth rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>After</label><div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">End balance</div><div className="result-value" data-testid="ann-end">{fmt(c.end)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Starting principal</td><td className="text-right">{fmt(start)}</td></tr>
            <tr><td>Total additions</td><td className="text-right">{fmt(c.totalAdd)}</td></tr>
            <tr><td>Total return/interest earned</td><td className="text-right text-orange-600">{fmt(c.totalReturn)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Starting principal',value:start,color:'#2563eb'},{label:'Additions',value:c.totalAdd,color:'#10b981'},{label:'Return/interest',value:c.totalReturn,color:'#dc2626'}]} size={180}/></div>
        </div>
      </div>

      {/* Bar Graph — annual accumulation */}
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Balance Accumulation Graph</h3>
        <BarChart
          data={c.annual.map((r, i) => {
            const cumAdd = c.annual.slice(0, i + 1).reduce((a,b)=>a+b.deposit,0);
            const cumInt = c.annual.slice(0, i + 1).reduce((a,b)=>a+b.interest,0);
            return {
              label: String(r.year),
              values: [
                { value: start,   color: '#2563eb', label: 'Starting Principal' },
                { value: cumAdd,  color: '#10b981', label: 'Additions' },
                { value: cumInt,  color: '#f97316', label: 'Interest' },
              ]
            };
          })}
          stack={true}
          legend={[
            { label: 'Starting Principal', color: '#2563eb' },
            { label: 'Additions',          color: '#10b981' },
            { label: 'Interest',           color: '#f97316' },
          ]}
        />
      </div>
      <div className="calc-card p-5 mt-6">
        <div className="flex gap-2 mb-3">
          <button onClick={()=>setView('annual')} className={`px-4 py-1.5 rounded-full text-xs font-semibold ${view==='annual'?'bg-blue-700 text-white':'bg-slate-100 text-slate-700'}`}>Annual Schedule</button>
          <button onClick={()=>setView('monthly')} className={`px-4 py-1.5 rounded-full text-xs font-semibold ${view==='monthly'?'bg-blue-700 text-white':'bg-slate-100 text-slate-700'}`}>Monthly Schedule</button>
        </div>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white sticky top-0"><tr><th className="px-3 py-2 text-left">{view==='annual'?'Year':'Month'}</th><th className="px-3 py-2 text-right">Deposit</th><th className="px-3 py-2 text-right">Interest</th><th className="px-3 py-2 text-right">Ending balance</th></tr></thead>
            <tbody>
              {(view==='annual'?c.annual:c.monthly).map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-3 py-2">{view==='annual'?r.year:r.month}</td><td className="px-3 py-2 text-right">{fmt(r.deposit)}</td><td className="px-3 py-2 text-right text-orange-600">{fmt(r.interest)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(r.balance)}</td></tr>))}
            </tbody>
          </table>
        </div>
      </div>
    </CalcShell>
  );
}
